import { Component, OnDestroy, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,

  debounceTime,
  defer,
  distinctUntilChanged,
  expand,
  filter,
  finalize,
  fromEvent,
  interval,
  map,
  merge,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { DataFeedQuery, DataFeedSummary, DataFeedUpload, DataFeedView, ImportUploadActionResult, ImportUploadPreview } from '../../models/data-feed.model';
import { EtlMonitorService } from '../../services/etl-monitor.service';
import { DataFeedTraceDialogComponent } from './data-feed-trace-dialog/data-feed-trace-dialog.component';
import { IACopyReportDialogComponent } from './ia-copy-report-dialog/ia-copy-report-dialog.component';
import { DataFeedRunAction, DataFeedRunControl } from './data-feed-run-control';
import {
  DataFeedImportConfirmChoice,
  DataFeedImportConfirmDialogComponent,
  DataFeedImportConfirmDialogData,
} from './data-feed-import-confirm-dialog/data-feed-import-confirm-dialog.component';
import {
  BadgeMeta,
  describeHttpError,
  formatCompact,
  formatDuration,
  hasPendingControl,
  isActiveStage,
  PendingControlMeta,
  pendingControlMeta,
  person,
  relativeTime,
  safeParse,
  saveBlob,
  shortError,
  stageMeta,
  uploadDurationMs,
} from './data-feed-ui';

export type StageFilter = 'all' | 'active' | 'awaiting' | 'completed' | 'errors' | 'failed' | 'paused' | 'cancelled';

/** Poll interval while any upload is waiting or processing. */
const ACTIVE_POLL_MS = 3000;
/** Poll interval when everything in the window has finished. */
const IDLE_POLL_MS = 15000;
/** Wait after the last keystroke before searching on the server. */
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_KEY = 'dataFeedMonitor.pageSize';

/** What the table shows: the window, stage group, search and page (0-based, as the paginator counts). */
interface FeedState {
  hours: number;
  stage: StageFilter;
  search: string;
  pageIndex: number;
  pageSize: number;
}

function sameState(a: FeedState | null, b: FeedState): boolean {
  return !!a && a.hours === b.hours && a.stage === b.stage && a.search === b.search
    && a.pageIndex === b.pageIndex && a.pageSize === b.pageSize;
}

function storedPageSize(options: number[]): number {
  try {
    const value = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return options.includes(value) ? value : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

@Component({
  selector: 'app-data-feed-monitor',
  templateUrl: './data-feed-monitor.component.html',
  styleUrls: ['./data-feed-monitor.component.scss']
})
export class DataFeedMonitorComponent implements OnInit, OnDestroy {

  readonly displayedColumns: string[] = ['file', 'stage', 'progress', 'uploaded', 'started', 'duration', 'attempts', 'actions'];
  readonly windowOptions = [
    { hours: 24, label: 'Last 24 hours' },
    { hours: 72, label: 'Last 72 hours' },
    { hours: 168, label: 'Last 7 days' },
  ];
  readonly stageFilters: { value: StageFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'awaiting', label: 'Awaiting confirmation' },
    { value: 'completed', label: 'Completed' },
    { value: 'errors', label: 'With errors' },
    { value: 'failed', label: 'Failed' },
    { value: 'paused', label: 'Paused' },
    { value: 'cancelled', label: 'Cancelled / discarded' },
  ];
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly pageSizeOptions = [10, 25, 50, 100];

  view: DataFeedView | null = null;
  summary: DataFeedSummary | null = null;
  /** The page of rows bound to the table (row objects are reused across polls). */
  rows: DataFeedUpload[] = [];
  /** A new page, stage group, search or window is loading: the current rows are dimmed until it arrives. */
  pageLoading = false;

  initialLoading = true;
  refreshing = false;
  pollError: string | null = null;
  lastUpdated: number | null = null;
  now = Date.now();
  isUploading = false;
  liveMessage = '';
  /** Upload ids with a confirm / discard request in flight. */
  private readonly uploadActionsInFlight = new Set<string>();
  /** Upload ids whose package details are being fetched. */
  private readonly detailsOpening = new Set<string>();
  /** Upload ids whose zip is being downloaded. */
  private readonly downloading = new Set<string>();

  /** The search box as typed; it reaches the server after a short pause in typing. */
  searchText = '';

  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();
  private readonly paused$ = new BehaviorSubject<boolean>(false);
  private readonly searchInput$ = new Subject<string>();
  private readonly state$ = new BehaviorSubject<FeedState>({
    hours: 72,
    stage: 'all',
    search: '',
    pageIndex: 0,
    pageSize: storedPageSize(this.pageSizeOptions),
  });
  /** The state of the page on screen. */
  private shownState: FeedState | null = null;
  /** Row objects by Key so polls update rows in place instead of replacing them. */
  private readonly rowCache = new Map<string, DataFeedUpload>();
  private readonly previousStages = new Map<string, string>();
  /** Pause / Start / Stop requests (confirm, call, snack bar). */
  readonly runControl: DataFeedRunControl;

  constructor(
    private etlMonitorService: EtlMonitorService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private clipboard: Clipboard
  ) {
    this.runControl = new DataFeedRunControl(dialog, snackBar, etlMonitorService);
  }

  get paused(): boolean { return this.paused$.value; }
  get windowHours(): number { return this.state$.value.hours; }
  set windowHours(hours: number) {
    if (hours !== this.state$.value.hours) {
      this.updateState({ hours, pageIndex: 0 });
    }
  }
  get stageFilter(): StageFilter { return this.state$.value.stage; }
  get pageIndex(): number { return this.state$.value.pageIndex; }
  get pageSize(): number { return this.state$.value.pageSize; }

  get hasActiveUploads(): boolean {
    // The server looks at the whole window (and pending pause / stop / start requests on the page), so an import
    // running on another page still keeps the fast poll.
    return this.view?.HasActiveUploads
      ?? !!this.view?.Uploads?.some(u => isActiveStage(u.Stage) || hasPendingControl(u));
  }

  /** Uploads in the window, before the stage group and search. */
  get totalUploads(): number {
    return this.view?.TotalUploads ?? this.view?.Uploads?.length ?? 0;
  }

  /** Uploads matching the stage group and search, across all pages. */
  get matchingUploads(): number {
    return this.view?.TotalCount ?? this.rows.length;
  }

  /** "26-50", the 1-based range of rows on screen. */
  get shownRange(): string {
    if (this.rows.length === 0) { return '0'; }
    const first = this.pageIndex * this.pageSize + 1;
    return `${first.toLocaleString('en-US')}-${(first + this.rows.length - 1).toLocaleString('en-US')}`;
  }

  get isFiltered(): boolean {
    return this.stageFilter !== 'all' || !!this.state$.value.search;
  }

  ngOnInit(): void {
    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => !document.hidden),
      distinctUntilChanged()
    );

    this.searchInput$.pipe(
      map(text => text.trim()),
      debounceTime(SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(search => {
      if (search !== this.state$.value.search) {
        this.updateState({ search, pageIndex: 0 });
      }
    });

    combineLatest([visible$, this.paused$, this.state$]).pipe(
      switchMap(([visible, paused, state]) => {
        if (!visible) {
          return EMPTY;
        }
        if (paused) {
          // Paused: no polling. A new page, filter or window still loads once; otherwise only on an explicit refresh.
          const now$ = sameState(this.shownState, state) ? EMPTY : of(undefined);
          return merge(now$, this.refresh$).pipe(switchMap(() => this.fetch(state)));
        }
        // Live: fetch now (and on manual refresh), then keep polling on an adaptive interval.
        return this.refresh$.pipe(
          startWith(undefined),
          switchMap(() => this.fetch(state).pipe(
            expand(() => timer(this.hasActiveUploads ? ACTIVE_POLL_MS : IDLE_POLL_MS).pipe(
              switchMap(() => this.fetch(state))
            ))
          ))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    // Drives "updated Xs ago" and the live-ticking durations of running uploads.
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => this.now = Date.now());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  private fetch(state: FeedState): Observable<DataFeedView | null> {
    return defer(() => {
      this.refreshing = true;
      this.pageLoading = !sameState(this.shownState, state) && !this.initialLoading;
      const query: DataFeedQuery = {
        hours: state.hours,
        stage: state.stage,
        search: state.search,
        page: state.pageIndex + 1,
        pageSize: state.pageSize,
      };
      return this.etlMonitorService.getDataFeed(query);
    }).pipe(
      tap(view => this.applyView(view, state)),
      catchError(err => {
        this.refreshing = false;
        this.pageLoading = false;
        this.initialLoading = false;
        this.pollError = describeHttpError(err, 'Could not refresh the data feed');
        return of(null);
      })
    );
  }

  /** Changes what the table shows; the pipeline in ngOnInit fetches it. */
  private updateState(change: Partial<FeedState>): void {
    this.state$.next({ ...this.state$.value, ...change });
  }

  private applyView(view: DataFeedView, state: FeedState): void {
    const uploads = view?.Uploads ?? [];
    const announcements: string[] = [];
    const seen = new Set<string>();

    const merged = uploads.map(incoming => {
      seen.add(incoming.Key);
      const previousStage = this.previousStages.get(incoming.Key);
      if (this.lastUpdated !== null && previousStage && previousStage !== incoming.Stage) {
        announcements.push(this.describeStageChange(incoming));
      }
      this.previousStages.set(incoming.Key, incoming.Stage);

      const existing = this.rowCache.get(incoming.Key);
      if (existing) {
        Object.assign(existing, incoming);
        return existing;
      }
      this.rowCache.set(incoming.Key, incoming);
      return incoming;
    });

    // Drop rows that left the page.
    for (const key of Array.from(this.rowCache.keys())) {
      if (!seen.has(key)) {
        this.rowCache.delete(key);
        this.previousStages.delete(key);
      }
    }

    this.view = { ...view, Uploads: merged };
    this.summary = view?.Summary ?? null;
    this.lastUpdated = Date.now();
    this.now = this.lastUpdated;
    this.pollError = null;
    this.initialLoading = false;
    this.refreshing = false;
    if (announcements.length) {
      this.liveMessage = announcements.slice(0, 3).join('. ') + (announcements.length > 3 ? `. ${announcements.length - 3} more updates` : '');
    }
    this.rows = merged;
    this.pageLoading = false;
    this.shownState = state;

    // Rows finished or left the window and the page is now past the end: the server sent the last page instead.
    const shownIndex = (view?.Page ?? 1) - 1;
    if (view?.PageSize && shownIndex !== state.pageIndex) {
      this.shownState = { ...state, pageIndex: shownIndex };
      this.updateState({ pageIndex: shownIndex });
    }
  }

  private describeStageChange(upload: DataFeedUpload): string {
    const name = upload.FileName || upload.DisplayName || upload.RunId || 'Upload';
    switch (upload.Stage) {
      case 'AwaitingConfirmation': return `${name} is waiting for confirmation`;
      case 'Queued': return `${name} queued`;
      case 'Processing': return `${name} started processing`;
      case 'Completed': return `${name} completed`;
      case 'CompletedWithErrors': return `${name} completed with errors`;
      case 'Failed': return `${name} failed`;
      case 'Rejected': return `${name} was rejected`;
      case 'Paused': return `${name} paused`;
      case 'Cancelled': return `${name} was cancelled`;
      case 'Discarded': return `${name} was discarded`;
      default: return `${name} is ${stageMeta(upload.Stage).label.toLowerCase()}`;
    }
  }

  // ---------------------------------------------------------------------------
  // Toolbar actions
  // ---------------------------------------------------------------------------

  refresh(): void {
    if (document.hidden) { return; }
    this.refresh$.next();
  }

  togglePaused(): void {
    this.paused$.next(!this.paused$.value);
  }

  onStageFilterChange(value: StageFilter): void {
    if (value !== this.stageFilter) {
      this.updateState({ stage: value, pageIndex: 0 });
    }
  }

  onSearchChange(text: string): void {
    this.searchInput$.next(text ?? '');
  }

  clearSearch(): void {
    this.searchText = '';
    this.searchInput$.next('');
    if (this.state$.value.search) {
      this.updateState({ search: '', pageIndex: 0 });
    }
  }

  onPage(event: PageEvent): void {
    if (event.pageSize !== this.pageSize) {
      try { localStorage.setItem(PAGE_SIZE_KEY, String(event.pageSize)); } catch { /* per-viewer convenience only */ }
    }
    this.updateState({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  // ---------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------

  trackByKey(_: number, upload: DataFeedUpload): string {
    return upload.Key;
  }

  stageMeta(upload: DataFeedUpload): BadgeMeta {
    return stageMeta(upload.Stage);
  }

  updatedAgo(): string {
    return relativeTime(this.lastUpdated, this.now);
  }

  duration(upload: DataFeedUpload): string {
    return formatDuration(uploadDurationMs(upload, this.now));
  }

  entityProgress(upload: DataFeedUpload): number {
    return upload.EntitiesTotal > 0 ? Math.round((upload.EntitiesDone / upload.EntitiesTotal) * 100) : 0;
  }

  currentEntityIndex(upload: DataFeedUpload): number {
    return Math.min(upload.EntitiesTotal, upload.EntitiesDone + 1);
  }

  waitingCount(): number {
    return (this.summary?.Waiting ?? 0) + (this.summary?.Queued ?? 0);
  }

  awaitingCount(): number {
    return this.summary?.AwaitingConfirmation ?? 0;
  }

  isUploadBusy(upload: DataFeedUpload): boolean {
    return !!upload.UploadId && this.uploadActionsInFlight.has(upload.UploadId);
  }

  isOpeningDetails(upload: DataFeedUpload): boolean {
    return !!upload.UploadId && this.detailsOpening.has(upload.UploadId);
  }

  isDownloading(upload: DataFeedUpload): boolean {
    return !!upload.UploadId && this.downloading.has(upload.UploadId);
  }

  /** A person as shown in the list: the display name when known, else the e-mail. */
  person(name: string | null | undefined, email: string | null | undefined): string {
    return person(name, email);
  }

  /** Package details and download exist for portal uploads the upload log knows; a staged one has Review instead. */
  hasPackageDetails(upload: DataFeedUpload): boolean {
    return !!upload.HasPackageDetails && !!upload.UploadId && upload.Stage !== 'AwaitingConfirmation';
  }

  compact(value: number | null | undefined): string {
    return formatCompact(value);
  }

  exact(value: number | null | undefined): string {
    return (value ?? 0).toLocaleString('en-US');
  }

  failedCount(): number {
    return (this.summary?.Failed ?? 0) + (this.summary?.Rejected ?? 0);
  }

  /** Pending pause / cancel / start chip shown next to the stage badge, or null. */
  pendingControl(upload: DataFeedUpload): PendingControlMeta | null {
    return pendingControlMeta(upload);
  }

  /** Paused or cancelled part-way: show a static bar at the point where it stopped. */
  isHalted(upload: DataFeedUpload): boolean {
    return (upload.Stage === 'Paused' || upload.Stage === 'Cancelled') && upload.EntitiesTotal > 0;
  }

  canOpenIaReport(upload: DataFeedUpload): boolean {
    return !!upload.RunId
      && (upload.Stage === 'Completed' || upload.Stage === 'CompletedWithErrors')
      && upload.IsJaiEnabled === true;
  }

  // ---------------------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------------------

  onRowActivate(upload: DataFeedUpload, event?: Event): void {
    if (event && (event.target as HTMLElement)?.closest('button, a, input')) {
      return; // inner controls handle themselves
    }
    this.openTrace(upload);
  }

  openTrace(upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    if (!upload.RunId) { return; }
    this.dialog.open(DataFeedTraceDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'data-feed-trace-panel',
      autoFocus: 'dialog',
      data: { runId: upload.RunId, fileName: upload.FileName || upload.DisplayName || upload.RunId },
    });
  }

  openIaCopyReportDialog(upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    if (!upload.RunId) { return; }
    this.dialog.open(IACopyReportDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: { runId: upload.RunId, fileName: upload.FileName || upload.DisplayName || upload.RunId }
    });
  }

  /** Pause, start or stop an import: confirm, send, show the server's message, then refresh the feed. */
  controlRun(action: DataFeedRunAction, upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    this.runControl.run(action, upload).pipe(takeUntil(this.destroy$)).subscribe(() => this.refresh());
  }

  copy(text: string | null | undefined, what: string, event?: Event): void {
    event?.stopPropagation();
    if (!text) { return; }
    const ok = this.clipboard.copy(text);
    this.snackBar.open(ok ? `${what} copied to clipboard` : `Could not copy ${what.toLowerCase()}`, 'Close', { duration: 2000 });
  }

  // ---------------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------------

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files && files.length > 0) {
      this.uploadFile(files[0]);
    }
    // Allow picking the same file again.
    input.value = '';
  }

  /**
   * Two-step upload: the zip is staged and its manifest read; the user then sees what the import will do to existing
   * records, missing records and relationships (retaineditems) and confirms or discards it. Nothing is queued before
   * that. A zip without a readable manifest.json is rejected by the server at once.
   */
  uploadFile(file: File): void {
    if (!file) {
      this.snackBar.open('No file selected', 'Close', { duration: 3000 });
      return;
    }
    if (!/\.zip$/i.test(file.name)) {
      this.snackBar.open('Only .zip import packages can be uploaded', 'Close', { duration: 4000 });
      return;
    }

    this.isUploading = true;
    this.etlMonitorService.uploadFile(file).pipe(
      finalize(() => this.isUploading = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        this.refresh();
        if (response?.preview) {
          this.openImportConfirmation(response.preview);
        } else {
          this.snackBar.open(response?.message || 'File staged', 'Close', { duration: 4000 });
        }
      },
      error: (error) => {
        const body = error?.error;
        const errorMessage = body?.message || describeHttpError(error, 'File upload failed');
        this.snackBar.open(errorMessage, 'Close', { duration: 8000 });
        this.refresh();
        console.error('Upload error:', error);
      }
    });
  }

  /** The package details the user saw at confirmation: the manifest and the preview stored with the upload log entry. */
  openPackageDetails(upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    const uploadId = upload.UploadId;
    if (!uploadId || this.detailsOpening.has(uploadId)) { return; }
    this.detailsOpening.add(uploadId);
    this.etlMonitorService.getUploadHistoryEntry(uploadId).pipe(
      finalize(() => this.detailsOpening.delete(uploadId)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: detail => {
        const preview = safeParse(detail.PreviewJson) as ImportUploadPreview | string | null;
        if (!preview || typeof preview === 'string') {
          this.snackBar.open('No package details were stored for this upload.', 'Close', { duration: 5000 });
          return;
        }
        this.dialog.open<DataFeedImportConfirmDialogComponent, DataFeedImportConfirmDialogData>(DataFeedImportConfirmDialogComponent, {
          width: '980px',
          maxWidth: '96vw',
          maxHeight: '92vh',
          autoFocus: 'dialog',
          data: { preview, readOnly: true, entry: detail.Entry, manifestJson: detail.ManifestJson, run: detail.Run },
        });
      },
      error: err => this.snackBar.open(shortError(describeHttpError(err, 'Could not load the package details')), 'Close', { duration: 6000 }),
    });
  }

  /** The kept copy of a confirmed upload's zip. */
  downloadPackage(upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    const uploadId = upload.UploadId;
    if (!uploadId || !upload.CanDownload || this.downloading.has(uploadId)) { return; }
    this.downloading.add(uploadId);
    this.etlMonitorService.downloadUploadPackage(uploadId).pipe(
      finalize(() => this.downloading.delete(uploadId)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        if (response.body) {
          saveBlob(response.body, upload.FileName || 'package.zip');
        }
      },
      error: err => this.snackBar.open(shortError(describeHttpError(err, 'Could not download the package')), 'Close', { duration: 6000 }),
    });
  }

  /** Confirm action of an AwaitingConfirmation row: fetch its preview again and open the same dialog. */
  reviewUpload(upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    const uploadId = upload.UploadId;
    if (!uploadId || this.uploadActionsInFlight.has(uploadId)) { return; }
    this.uploadActionsInFlight.add(uploadId);
    this.etlMonitorService.getUploadPreview(uploadId).pipe(
      finalize(() => this.uploadActionsInFlight.delete(uploadId)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: preview => this.openImportConfirmation(preview),
      error: err => {
        this.snackBar.open(this.actionMessage(err, 'Could not load the upload preview'), 'Close', { duration: 6000 });
        this.refresh();
      },
    });
  }

  /** Discard action of an AwaitingConfirmation row, with its own confirmation. */
  discardUpload(upload: DataFeedUpload, event?: Event): void {
    event?.stopPropagation();
    const uploadId = upload.UploadId;
    if (!uploadId || this.uploadActionsInFlight.has(uploadId)) { return; }
    const name = upload.FileName || 'this upload';
    this.runControl.confirmDialog({
      title: 'Discard upload',
      icon: 'delete',
      warn: true,
      message: `Discard ${name}? The staged file is deleted and nothing is imported. Upload it again to load it later.`,
      confirmText: 'Discard',
    }).pipe(
      filter(choice => choice === true),
      switchMap(() => this.runUploadAction(uploadId, 'discard')),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private openImportConfirmation(preview: ImportUploadPreview): void {
    this.dialog.open<DataFeedImportConfirmDialogComponent, DataFeedImportConfirmDialogData, DataFeedImportConfirmChoice>(
      DataFeedImportConfirmDialogComponent, {
        width: '980px',
        maxWidth: '96vw',
        maxHeight: '92vh',
        autoFocus: 'first-tabbable',
        data: { preview },
      }
    ).afterClosed().pipe(
      take(1),
      filter((choice): choice is DataFeedImportConfirmChoice => choice === 'confirm' || choice === 'discard'),
      switchMap(choice => this.runUploadAction(preview.UploadId, choice)),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /** Confirm (queue) or discard a staged upload, show the server's message, refresh the feed. */
  private runUploadAction(uploadId: string, choice: DataFeedImportConfirmChoice): Observable<ImportUploadActionResult | null> {
    this.uploadActionsInFlight.add(uploadId);
    const call = choice === 'confirm'
      ? this.etlMonitorService.confirmUpload(uploadId)
      : this.etlMonitorService.discardUpload(uploadId);
    const failed = choice === 'confirm' ? 'Could not queue the import' : 'Could not discard the upload';
    return call.pipe(
      tap(result => this.snackBar.open(result?.Message || (choice === 'confirm' ? 'Import queued.' : 'Upload discarded.'), 'Close', { duration: 5000 })),
      catchError(err => {
        this.snackBar.open(this.actionMessage(err, failed), 'Close', { duration: 7000 });
        return of(null);
      }),
      finalize(() => {
        this.uploadActionsInFlight.delete(uploadId);
        this.refresh();
      })
    );
  }

  /** 404 / 409 carry an ImportUploadActionResult body; anything else becomes a generic message. */
  private actionMessage(err: any, fallback: string): string {
    const body = err?.error;
    const message = body?.Message ?? body?.message;
    return typeof message === 'string' && message ? message : describeHttpError(err, fallback);
  }
}
